import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

const CustomSelect = ({
    name,
    options = [],
    value,
    onChange,
    placeholder = "Select option",
    className = "",
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

    // 🔹 Calculate coordinates
    const updatePosition = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();

            const actualMenuHeight = menuRef.current
                ? menuRef.current.offsetHeight
                : Math.min(options.length * 36 + 8, 224);

            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            const shouldOpenTop = spaceBelow < actualMenuHeight && spaceAbove > spaceBelow;

            setCoords({
                left: rect.left,
                width: rect.width,
                top: shouldOpenTop
                    ? rect.top - actualMenuHeight - 4
                    : rect.bottom + 4,
            });
        }
    };

    useLayoutEffect(() => {
        if (open) {
            updatePosition();
        }
    }, [open, options]);

    useEffect(() => {
        if (open) {
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);
        }
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                const portalMenu = document.getElementById(`select-portal-${name}`);
                if (portalMenu && portalMenu.contains(e.target)) return;
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [name]);

    const handleKeyDown = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            setOpen(true);
            setFocusedIndex(0);
            return;
        }

        if (open) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setFocusedIndex((prev) => (prev + 1) % options.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setFocusedIndex((prev) =>
                    prev === 0 ? options.length - 1 : prev - 1
                );
            } else if (e.key === "Enter" && focusedIndex >= 0) {
                e.preventDefault();
                const selectedOption = options[focusedIndex];
                const optionValue = typeof selectedOption === 'object'
                    ? selectedOption.value
                    : selectedOption;
                onChange(name, optionValue);
                setOpen(false);
            } else if (e.key === "Escape") {
                setOpen(false);
            }
        }
    };

    const getDisplayValue = (val) => {
        if (!val) return placeholder;
        const option = options.find(opt => {
            if (typeof opt === 'object') {
                return opt.value === val;
            }
            return opt === val;
        });
        if (option) {
            return typeof option === 'object' ? option.label : option;
        }
        return val;
    };

    const getOptionLabel = (opt) => (typeof opt === 'object' ? opt.label : opt);
    const getOptionValue = (opt) => (typeof opt === 'object' ? opt.value : opt);
    const getOptionKey = (opt, index) => (typeof opt === 'object' ? opt.value : opt || index);

    return (
        <div ref={containerRef} className={`relative inline-block ${className}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                onKeyDown={handleKeyDown}
                className={`capitalize w-full text-left text-gray-600 border border-gray-300 bg-white 
                    rounded-md px-3 py-1 focus:ring-1 focus:ring-gray-400
                    focus:border-gray-400 transition-colors ${open ? "ring-1 ring-gray-400 border-gray-400" : ""
                    }`}
            >
                {getDisplayValue(value)}
            </button>

            {open && createPortal(
                <ul
                    ref={menuRef}
                    id={`select-portal-${name}`}
                    role="listbox"
                    tabIndex={-1}
                    style={{
                        position: 'fixed',
                        top: `${coords.top}px`,
                        left: `${coords.left}px`,
                        minWidth: `${coords.width}px`, // 🔹 Ensures menu is AT LEAST as wide as the button
                    }}
                    className="w-max max-w-xs whitespace-nowrap bg-white border border-gray-200 rounded-md shadow-lg z-[9999] max-h-56 overflow-auto"
                >
                    {options.map((opt, index) => {
                        const optionValue = getOptionValue(opt);
                        const optionLabel = getOptionLabel(opt);
                        const isSelected = value === optionValue;

                        return (
                            <li
                                key={getOptionKey(opt, index)}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                    onChange(name, optionValue);
                                    setOpen(false);
                                }}
                                onMouseEnter={() => setFocusedIndex(index)}
                                className={`capitalize px-3 py-2 cursor-pointer transition-colors 
                                    ${isSelected
                                        ? "bg-gray-50 text-gray-600 font-semibold"
                                        : focusedIndex === index
                                            ? "bg-gray-100 text-gray-700"
                                            : "hover:bg-gray-100 hover:text-gray-700"
                                    }`}
                            >
                                {optionLabel}
                            </li>
                        );
                    })}
                </ul>,
                document.body
            )}
        </div>
    );
};

export default CustomSelect;

// import { useState, useRef, useEffect } from "react";

// const CustomSelect = ({
//     name,
//     options = [],
//     value,
//     onChange,
//     placeholder = "Select option",
// }) => {
//     const [open, setOpen] = useState(false);
//     const [openTop, setOpenTop] = useState(false); // 🔹 State to handle direction
//     const containerRef = useRef(null);
//     const [focusedIndex, setFocusedIndex] = useState(-1);

//     // 🔹 Calculate available space when dropdown toggles open
//     useEffect(() => {
//         if (open && containerRef.current) {
//             const rect = containerRef.current.getBoundingClientRect();
//             const spaceBelow = window.innerHeight - rect.bottom;
//             const dropdownHeight = 230; // Matches max-h-56 (224px + margins)

//             // Opens on top if space below is less than dropdown height AND top space is larger
//             if (spaceBelow < dropdownHeight && rect.top > spaceBelow) {
//                 setOpenTop(true);
//             } else {
//                 setOpenTop(false);
//             }
//         }
//     }, [open]);

//     // 🔹 Close dropdown when clicking outside
//     useEffect(() => {
//         const handleClickOutside = (e) => {
//             if (containerRef.current && !containerRef.current.contains(e.target)) {
//                 setOpen(false);
//             }
//         };
//         document.addEventListener("mousedown", handleClickOutside);
//         return () => document.removeEventListener("mousedown", handleClickOutside);
//     }, []);

//     // 🔹 Keyboard navigation
//     const handleKeyDown = (e) => {
//         if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
//             setOpen(true);
//             setFocusedIndex(0);
//             return;
//         }

//         if (open) {
//             if (e.key === "ArrowDown") {
//                 e.preventDefault();
//                 setFocusedIndex((prev) => (prev + 1) % options.length);
//             } else if (e.key === "ArrowUp") {
//                 e.preventDefault();
//                 setFocusedIndex((prev) =>
//                     prev === 0 ? options.length - 1 : prev - 1
//                 );
//             } else if (e.key === "Enter" && focusedIndex >= 0) {
//                 e.preventDefault();
//                 const selectedOption = options[focusedIndex];
//                 const optionValue = typeof selectedOption === 'object'
//                     ? selectedOption.value
//                     : selectedOption;
//                 onChange(name, optionValue);
//                 setOpen(false);
//             } else if (e.key === "Escape") {
//                 setOpen(false);
//             }
//         }
//     };

//     // Helper to get display value
//     const getDisplayValue = (val) => {
//         if (!val) return placeholder;
//         const option = options.find(opt => {
//             if (typeof opt === 'object') {
//                 return opt.value === val;
//             }
//             return opt === val;
//         });
//         if (option) {
//             return typeof option === 'object' ? option.label : option;
//         }
//         return val;
//     };

//     // Helper to get option label
//     const getOptionLabel = (opt) => {
//         return typeof opt === 'object' ? opt.label : opt;
//     };

//     // Helper to get option value
//     const getOptionValue = (opt) => {
//         return typeof opt === 'object' ? opt.value : opt;
//     };

//     // Helper to get option key
//     const getOptionKey = (opt, index) => {
//         return typeof opt === 'object' ? opt.value : opt || index;
//     };

//     return (
//         <div ref={containerRef} className="relative w-full">
//             <button
//                 type="button"
//                 onClick={() => setOpen(!open)}
//                 onKeyDown={handleKeyDown}
//                 onAbort={(e) => console.log(e)}
//                 className={`capitalize w-full text-left text-gray-600 border border-gray-300 bg-white
//                     rounded-md px-3 py-1 focus:ring-1 focus:ring-gray-400
//                     focus:border-gray-400 transition-colors ${open ? "ring-1 ring-gray-400 border-gray-400" : ""
//                     }`}
//             >
//                 {getDisplayValue(value)}
//             </button>

//             {open && (
//                 <ul
//                     role="listbox"
//                     tabIndex={-1}
//                     className={`absolute w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-56 overflow-auto ${openTop ? "bottom-full mb-1" : "top-full mt-1"
//                         }`}
//                 >
//                     {options.map((opt, index) => {
//                         const optionValue = getOptionValue(opt);
//                         const optionLabel = getOptionLabel(opt);
//                         const isSelected = value === optionValue;

//                         return (
//                             <li
//                                 key={getOptionKey(opt, index)}
//                                 role="option"
//                                 aria-selected={isSelected}
//                                 onClick={() => {
//                                     onChange(name, optionValue);
//                                     setOpen(false);
//                                 }}
//                                 onMouseEnter={() => setFocusedIndex(index)}
//                                 className={`capitalize px-3 py-2 cursor-pointer transition-colors
//                                     ${isSelected
//                                         ? "bg-gray-50 text-gray-600"
//                                         : focusedIndex === index
//                                             ? "bg-gray-100 text-gray-700"
//                                             : "hover:bg-gray-100 hover:text-gray-700"
//                                     }`}
//                             >
//                                 {optionLabel}
//                             </li>
//                         );
//                     })}
//                 </ul>
//             )}
//         </div>
//     );
// };

// export default CustomSelect;